import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeButton } from '@/components/ui/tee-button';
import { LocationSwitcher } from '@/components/organisms/LocationSwitcher/LocationSwitcher';
import { useGamePlanLocation } from '@/hooks/useGamePlanLocation';
import { useGamePlanOverview, useJourney } from '@/hooks/useGamePlanTracker';
import { OnboardingWizard } from './components/OnboardingWizard';
import { AdjustPlanButton } from './components/AdjustPlanButton';
import type { GameJourney, JourneyPhase } from '@/types/gamePlanTracker';

const micro =
  'text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]';

export default function OverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locations, locationId, setLocationId, currentLocation } =
    useGamePlanLocation();

  const { data: overview, isLoading: ovLoading, refetch } =
    useGamePlanOverview(locationId);
  const onboarded = overview?.onboarded ?? false;
  const { data: journey, isLoading } = useJourney(
    onboarded ? locationId : undefined,
  );
  const switcher =
    locations && locations.length > 1 && locationId ? (
      <LocationSwitcher
        currentLocationId={locationId}
        currentLocationName={currentLocation?.name ?? ''}
        onSelect={setLocationId}
      />
    ) : undefined;

  return (
    <div className="section_container">
      {switcher && <div className="flex justify-end mb-3">{switcher}</div>}
      {!locationId || ovLoading ? (
        <TeeLoader size="md" text={t('common.loading', 'Loading…')} />
      ) : !onboarded ? (
        <OnboardingWizard
          locationId={locationId}
          defaultGoal={overview?.defaults?.foundingMemberGoal ?? 50}
          defaultDate={overview?.defaults?.grandOpeningDate ?? ''}
          onComplete={() => refetch()}
        />
      ) : isLoading || !journey ? (
        <TeeLoader size="md" text={t('common.loading', 'Loading…')} />
      ) : (
        <div className="flex flex-col gap-1">
          <JourneyArc
            journey={journey}
            locationName={currentLocation?.name}
            adjust={<AdjustPlanButton locationId={locationId} tone="forest" />}
          />
          <div className="relative mt-6">
            <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-[var(--border-default)]" />
            {journey.phases.map((p) => (
              <PhaseChapter key={p.key} p={p} members={journey.members} />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <TeeButton
              variant="primary"
              size="m"
              text="Go to this week →"
              onClick={() => navigate('/game-plan/current-week')}
            />
            <TeeButton
              variant="secondary"
              size="m"
              text="Open the Goal Board →"
              onClick={() => navigate('/game-plan/goal-board')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function JourneyArc({
  journey,
  locationName,
  adjust,
}: {
  journey: GameJourney;
  locationName?: string;
  adjust?: React.ReactNode;
}) {
  const { goal, members, currentWeek, weeksLeft, launchLabel, memberHistory, phases } =
    journey;

  // Histogram geometry: one column per week (1–16), climbing toward the goal
  // band at the top. A little headroom above the goal so the band isn't flush.
  const W = 320;
  const TOP = 12;
  const BOTTOM = 86;
  const CH = BOTTOM - TOP;
  const yMax = goal * 1.08;
  const colW = W / 16;
  const yFor = (v: number) => BOTTOM - Math.min(1, v / yMax) * CH;

  // Expected cumulative members at the end of a week (interpolated through the
  // phase milestones: 0 at wk0, each phase target at its last week).
  const milestones: [number, number][] = [[0, 0], ...phases.map((p) => [p.last, p.targetMembers] as [number, number])];
  const expectedAt = (week: number): number => {
    for (let i = 1; i < milestones.length; i++) {
      const [w0, v0] = milestones[i - 1];
      const [w1, v1] = milestones[i];
      if (week <= w1) return v0 + ((v1 - v0) * (week - w0)) / (w1 - w0);
    }
    return goal;
  };
  const goalY = yFor(goal);

  return (
    <div className="relative overflow-hidden rounded-[14px] bg-[var(--color-forest)] text-[var(--text-on-forest)] p-6">
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(rgba(251,246,236,0.1) 1.1px, transparent 1.1px)',
          backgroundSize: '15px 15px',
        }}
        aria-hidden
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gold-500)]">
              The 16-week arc{locationName ? ` · ${locationName}` : ''}
            </div>
            <h2 className="text-[24px] font-bold mt-1">Week {currentWeek} of 16</h2>
            <div className="text-body-s text-[var(--text-on-forest-muted)] mt-0.5">
              {weeksLeft} weeks to Grand Opening · {launchLabel}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-on-forest-muted)]">
              Founding members
            </div>
            <div className="text-[30px] font-bold">
              {members}
              <span className="text-[15px] text-[var(--text-on-forest-muted)]">
                {' '}
                / {goal}
              </span>
            </div>
            {adjust && <div className="mt-2 flex justify-end">{adjust}</div>}
          </div>
        </div>

        <svg
          viewBox={`0 0 ${W} 100`}
          className="w-full h-[170px]"
          preserveAspectRatio="none"
        >
          {/* goal band */}
          <rect x={0} y={goalY - 1.5} width={W} height={3} fill="rgba(181,143,45,0.18)" />
          <line
            x1={0}
            y1={goalY}
            x2={W}
            y2={goalY}
            stroke="var(--color-gold-500)"
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          {/* baseline */}
          <line x1={0} y1={BOTTOM} x2={W} y2={BOTTOM} stroke="rgba(251,246,236,0.2)" strokeWidth={1} />
          {/* columns: 1–16 */}
          {Array.from({ length: 16 }, (_, i) => {
            const week = i + 1;
            const bx = i * colW + colW * 0.2;
            const bw = colW * 0.6;
            const actual = week <= currentWeek ? memberHistory[week - 1] ?? 0 : null;
            if (actual != null) {
              const yy = yFor(actual);
              return (
                <rect
                  key={week}
                  x={bx}
                  y={yy}
                  width={bw}
                  height={Math.max(1, BOTTOM - yy)}
                  rx={1}
                  fill={week === currentWeek ? 'var(--color-gold-500)' : 'var(--text-on-forest)'}
                  opacity={week === currentWeek ? 1 : 0.85}
                />
              );
            }
            // future week: faint expected trajectory
            const yy = yFor(expectedAt(week));
            return (
              <rect
                key={week}
                x={bx}
                y={yy}
                width={bw}
                height={Math.max(1, BOTTOM - yy)}
                rx={1}
                fill="rgba(251,246,236,0.12)"
              />
            );
          })}
        </svg>
        <div className="flex items-center justify-between text-[10px] text-[var(--text-on-forest-muted)] mt-1.5 px-0.5">
          <span>Wk 1</span>
          <span className="text-[var(--color-gold-500)] font-semibold">
            Now · Week {currentWeek} ({members} members)
          </span>
          <span>Goal {goal} · Grand Opening</span>
        </div>
      </div>
    </div>
  );
}

const pill: Record<JourneyPhase['status'], { label: string; cls: string }> = {
  done: {
    label: 'Done',
    cls: 'bg-[rgba(12,61,45,0.08)] text-[var(--color-forest)]',
  },
  current: {
    label: "You're here",
    cls: 'bg-[rgba(181,143,45,0.18)] text-[var(--color-text-gold)]',
  },
  upcoming: {
    label: 'Coming up',
    cls: 'bg-[rgba(22,36,30,0.06)] text-[var(--text-content-sub-title)]',
  },
};

function PhaseChapter({ p, members }: { p: JourneyPhase; members: number }) {
  const node =
    p.status === 'done'
      ? 'bg-[var(--color-forest)] border-[var(--color-forest)] text-[var(--text-on-forest)]'
      : p.status === 'current'
      ? 'bg-[var(--color-gold-500)] border-[var(--color-gold-500)] text-[#3a2d08]'
      : 'bg-[var(--background-card)] border-[var(--border-default)] text-[var(--text-content-sub-title)]';
  const st = pill[p.status];
  return (
    <div className="relative flex gap-4 pb-4">
      <div
        className={
          'w-8 h-8 rounded-full flex-none flex items-center justify-center font-bold text-[13px] border-2 z-[1] ' +
          node
        }
      >
        {p.status === 'done' ? <Check size={16} /> : p.first}
      </div>
      <div
        className={
          'flex-1 rounded-[12px] border bg-[var(--background-card)] p-4 ' +
          (p.status === 'current'
            ? 'border-[var(--color-forest)] shadow-[0_0_0_1px_var(--color-forest)]'
            : 'border-[var(--border-default)]')
        }
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-heading-h5 text-[var(--text-content-title)]">
              {p.name}
            </div>
            <div className="text-[12px] text-[var(--text-content-sub-title)] mt-0.5">
              {p.label} · {p.dateRange}
            </div>
          </div>
          <span
            className={
              'text-[11px] font-semibold rounded-full px-2.5 py-1 whitespace-nowrap ' +
              st.cls
            }
          >
            {st.label}
          </span>
        </div>
        <div className="text-body-s font-semibold text-[var(--color-text-gold)] mt-2 mb-1.5">
          Target by end: {p.targetMembers} members
          {p.index === 4 ? ' · 🎉 Grand Opening' : ''}
        </div>
        <div className="flex flex-col gap-1">
          {p.bullets.map((b, i) => (
            <div
              key={i}
              className="text-body-s text-[var(--text-content-title)] flex gap-2"
            >
              <span className="text-[var(--color-text-gold)]">›</span>
              <span>{b}</span>
            </div>
          ))}
        </div>
        {p.status !== 'upcoming' && (
          <div className="flex gap-6 mt-3 pt-3 border-t border-dashed border-[var(--border-default)]">
            <Stat k="To-dos done" v={String(p.tasksDone)} />
            <Stat k="Activities logged" v={String(p.activitiesLogged)} />
            {p.status === 'current' && <Stat k="Members" v={String(members)} />}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div className={micro}>{k}</div>
      <div className="text-heading-h5 text-[var(--text-content-title)]">{v}</div>
    </div>
  );
}
